import React, { useState } from "react";
import {
  SelectCorrectImageActivity,
  SpeakAndSelectLessonOption,
  IActivityFormProps,
} from "../../types";
import showToast from "@utils/toast";

import { Edit, Trash2, Save, X, Image as ImageIcon, Music } from "lucide-react";
import { getAssetUrl, getNextSerialId } from "@utils/id.generator";
import { useActivityCoreData } from "src/hooks/useActivityCoreData";
import { newActivity, updateActivity } from "src/service/activities.services";
import ActivityCore from "./activities.core";
import { useNavigate } from "react-router-dom";

const SelectCorrectImageActivityForm = ({
  lessonMeta,
  initialActivity,
  activityId,
}: IActivityFormProps<SelectCorrectImageActivity>) => {
  const isEditing = !!initialActivity;

  const navigate = useNavigate();

  const [editIdx, setEditIdx] = useState<number | null>(null);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [answer, setAnswer] = useState(initialActivity?.data.answer || "");
  const [audioUrl, setAudioUrl] = useState(
    initialActivity?.data.question.audioUrl || ""
  );
  const [options, setOptions] = useState<SpeakAndSelectLessonOption[]>(
    initialActivity?.data.options || []
  );
  const [newOption, setNewOption] = useState({
    imageUrl: "",
    text: "",
    audioUrl: "",
  });

  const QuestionOptionsState: SpeakAndSelectLessonOption[] = options.map(
    (opt) => {
      if (!opt.audioUrl && !opt.text) {
        return {
          id: opt.id,
          imageUrl: opt.imageUrl,
        } as SpeakAndSelectLessonOption;
      }

      if (!opt.text && opt.audioUrl) {
        return {
          id: opt.id,
          imageUrl: opt.imageUrl,
          audioUrl: opt.audioUrl,
        } as SpeakAndSelectLessonOption;
      }

      if (!opt.audioUrl && opt.text) {
        return {
          id: opt.id,
          imageUrl: opt.imageUrl,
          text: opt.text,
        } as SpeakAndSelectLessonOption;
      }

      return {
        id: opt.id,
        imageUrl: opt.imageUrl,
        text: opt.text,
        audioUrl: opt.audioUrl,
      } as SpeakAndSelectLessonOption;
    }
  );

  const optionId = getNextSerialId(options);
  const addOption = () => {
    if (!newOption.imageUrl.trim()) {
      return showToast("Graphic code is required for each option", false);
    }

    if (newOption.text.trim().length > 17 || newOption.text.trim().length < 2) {
      return showToast(
        "Text code must be at least 2 characters and at most 17 characters",
        false
      );
    }

    const option: SpeakAndSelectLessonOption = {
      id: optionId,
      imageUrl: newOption.imageUrl,
      text: newOption.text?.trim() || undefined,
      audioUrl: newOption.audioUrl?.trim() || undefined,
    };
    setOptions((prev) => [...prev, option]);
    setNewOption({ imageUrl: "", text: "", audioUrl: "" });
  };

  const removeOption = (id: string) => {
    setOptions((prev) => prev.filter((opt) => opt.id !== id));
    if (answer === id) setAnswer("");
  };

  const coreState = useActivityCoreData({
    instruction: initialActivity?.instruction,
    localizedInstruction: initialActivity?.localizedInstruction,
    difficulty: initialActivity?.difficulty,
    tags: initialActivity?.tags,
    isActive: initialActivity?.isActive || false,
    activityNumber: initialActivity?.activityNumber || 0,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    setLoading(true);
    e.preventDefault();
    const isValidId = isEditing ? initialActivity?.id : activityId;
    if (!isValidId || isValidId === null || isValidId === undefined) {
      setLoading(false);
      return showToast("Activity Id not provided", false);
    }

    const payload: SelectCorrectImageActivity = {
      id: isValidId,
      activityKind: "SelectCorrectImageActivity",
      instruction: coreState.coreData.instruction,
      localizedInstruction: coreState.coreData.localizedInstruction,
      activityNumber: coreState.coreData.activityNumber,
      lessonId: initialActivity?.lessonId ?? (lessonMeta?.lesson as string),
      difficulty: coreState.coreData.difficulty,
      tags: coreState.coreData.tags,
      isActive: coreState.coreData.isActive,
      data: {
        question: { audioUrl },
        options: QuestionOptionsState,
        answer: answer || "",
      },
    };

    const request = isEditing
      ? updateActivity(payload, isValidId)
      : newActivity(payload);
    const response = await request;
    if (response?.status !== "success") {
      showToast("There was error saving activity", false);
      setLoading(false);
    } else {
      setLoading(false);
      showToast(
        `${isEditing ? "Activity Updated" : "Activity Created"} successfully`,
        true
      );

      return navigate("/dashboard/levels/lessons/activities");
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-8 max-w-4xl mx-auto bg-white p-6 rounded-lg shadow-md"
    >
      <h2 className="text-base font-bold text-gray-800">
        {isEditing ? "Edit" : "Create"} Select Correct Image Activity
      </h2>

      {/* Core Activity Ui */}
      <ActivityCore data={coreState} />

      <div>
        <label className="text-sm font-medium text-gray-700">Audio Code</label>
        <input
          value={audioUrl}
          onChange={(e) => setAudioUrl(e.target.value)}
          className="input-style mt-1"
          placeholder="Paste audio Code"
          type="text"
          required
          minLength={6}
          maxLength={6}
        />
      </div>

      {audioUrl && (
        <div className="pt-2">
          <audio controls className="w-full">
            <source src={getAssetUrl("audio", audioUrl)} type="audio/mpeg" />
          </audio>
        </div>
      )}

      <div className="space-y-4">
        <h3 className="text-md font-semibold text-gray-800">Add New Option</h3>
        <div className="grid sm:grid-cols-3 gap-4">
          <input
            placeholder="Graphic Code"
            value={newOption.imageUrl}
            minLength={6}
            maxLength={6}
            onChange={(e) =>
              setNewOption({ ...newOption, imageUrl: e.target.value })
            }
            className="input-style"
          />
          <input
            placeholder="Text (optional)"
            value={newOption.text}
            onChange={(e) =>
              setNewOption({ ...newOption, text: e.target.value })
            }
            className="input-style"
          />
          <input
            placeholder="Audio Code (optional)"
            value={newOption.audioUrl}
            minLength={6}
            maxLength={6}
            onChange={(e) =>
              setNewOption({ ...newOption, audioUrl: e.target.value })
            }
            className="input-style"
          />
        </div>
        <button
          type="button"
          onClick={addOption}
          className="text-sm text-brand hover:underline"
        >
          + Add Option
        </button>
      </div>

      {options.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-gray-800">
            Option Preview
          </h3>
          <div className="flex gap-3 overflow-x-auto pb-2">
            {options.map((opt, idx) => {
              const isEditing = editIdx === idx;
              return (
                <div
                  key={opt.id}
                  className="min-w-[200px] max-w-[200px] flex-shrink-0 border border-gray-200 rounded-lg bg-white shadow-md hover:shadow-lg transition"
                >
                  {isEditing ? (
                    <div className="p-3 space-y-2 text-xs">
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={opt.imageUrl || ""}
                          onChange={(e) =>
                            setOptions((prev) =>
                              prev.map((o, i) =>
                                i === idx
                                  ? { ...o, imageUrl: e.target.value }
                                  : o
                              )
                            )
                          }
                          className="w-full px-2 py-1 border rounded-md focus:ring focus:ring-brand"
                          placeholder="Image Code"
                        />
                      </div>

                      <input
                        type="text"
                        value={opt.text || ""}
                        onChange={(e) =>
                          setOptions((prev) =>
                            prev.map((o, i) =>
                              i === idx ? { ...o, text: e.target.value } : o
                            )
                          )
                        }
                        className="w-full px-2 py-1 border rounded-md focus:ring focus:ring-brand"
                        placeholder="Option Text"
                      />

                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={opt.audioUrl || ""}
                          onChange={(e) =>
                            setOptions((prev) =>
                              prev.map((o, i) =>
                                i === idx
                                  ? { ...o, audioUrl: e.target.value }
                                  : o
                              )
                            )
                          }
                          className="w-full px-2 py-1 border rounded-md focus:ring focus:ring-brand"
                          placeholder="Audio Code"
                        />
                      </div>

                      <div className="flex justify-end gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => setEditIdx(null)}
                          className="text-green-600 hover:text-green-800"
                          title="Save"
                        >
                          <Save size={16} />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditIdx(null)}
                          className="text-gray-500 hover:text-gray-700"
                          title="Cancel"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {/* Here would handle image preview for linguist */}
                      <div className="h-24 bg-gray-100 rounded-t overflow-hidden flex items-center justify-center">
                        {opt.imageUrl ? (
                          <img
                            src={getAssetUrl("graphic", opt.imageUrl)}
                            alt=""
                            className="object-cover w-full h-full"
                          />
                        ) : (
                          <span className="text-xs text-gray-400">
                            No Image
                          </span>
                        )}
                      </div>

                      <div className="p-2 space-y-2 text-xs">
                        {opt.text && (
                          <p className="font-medium text-gray-700 truncate">
                            {opt.text}
                          </p>
                        )}
                        {opt.audioUrl && (
                          <audio controls className="w-full h-7 rounded">
                            <source
                              src={getAssetUrl("audio", opt.audioUrl)}
                              type="audio/mpeg"
                            />
                          </audio>
                        )}

                        <div className="flex justify-between items-center border-t pt-2">
                          <label className="inline-flex items-center gap-1 text-[11px] text-gray-600">
                            <input
                              type="radio"
                              name="correctAnswer"
                              checked={answer === opt.id}
                              onChange={() => setAnswer(opt.id)}
                              className="text-emerald-600 scale-90"
                            />
                            Correct
                          </label>
                          <div className="flex gap-2">
                            <button
                              type="button"
                              onClick={() => setEditIdx(idx)}
                              className="text-blue-500 hover:text-blue-700"
                              title="Edit"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              type="button"
                              onClick={() => removeOption(opt.id)}
                              className="text-red-500 hover:text-red-700"
                              title="Remove"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="pt-4">
        <button type="submit" className="button-primary" disabled={isLoading}>
          {isEditing ? "Update Activity" : "Create Activity"}
        </button>
      </div>
    </form>
  );
};

export default SelectCorrectImageActivityForm;
